import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  query, 
  where,
  updateDoc,
  setDoc
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import MusicTool from "../../../components/tools/MusicTool";
import VideoTool from "../../../components/tools/VideoTool";
import topics from "../../../data/ca-topics/ca-5th-educator-other-topics";
import { markProgress } from "../../../utils/markProgress";

// --- Overlay styles (same visual language) ---
const overlayStyles = {
  layer: { position:'fixed', inset:0, pointerEvents:'none', zIndex:9999 },
  bubble: { position:'fixed', maxWidth:360, background:'#111', color:'#fff', fontSize:14, lineHeight:1.35, padding:12, borderRadius:8, boxShadow:'0 10px 30px rgba(0,0,0,0.35)', border:'2px solid #fff', pointerEvents:'auto', zIndex:9999 },
  arrow: { position:'fixed', width:0, height:0, borderLeft:'8px solid transparent', borderRight:'8px solid transparent', borderTop:'8px solid #111', pointerEvents:'none', zIndex:9999 },
  dismissButton: { position:'fixed', bottom:20, right:20, zIndex:10000, background:'#222', color:'#fff', border:'2px solid #fff', borderRadius:999, padding:'10px 16px', fontWeight:700, cursor:'pointer', boxShadow:'0 6px 14px rgba(0,0,0,0.35)' },
  talkBtn: { marginTop:8, background:'#fff', color:'#111', border:'2px solid #111', borderRadius:8, fontWeight:700, padding:'6px 10px', cursor:'pointer' },
  talkImgBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    borderRadius: 999,
    boxShadow: "0 3px 0 rgba(0,0,0,0.25)",
    transform: "translateY(0)",
  },
  talkImg: {
    width: 80,        // small version for tips
    height: "auto",
    display: "block",
    filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.25))",
  },
  stopRedSmall: {
    background: "#ff4d4d",
    color: "#fff",
    border: "2px solid #111",
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 3px 0 rgba(0,0,0,0.25)",
  },
};

const talkBtnImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";

function TalkHintsOverlay({ targets, onDismiss, canDismiss }) {
  const [positions, setPositions] = React.useState({});
  const [activeKey, setActiveKey] = React.useState(null); // <-- track which tip is speaking
  const { speak, stop } = useSpeech();

  const compute = () => {
    const next = {};
    Object.entries(targets).forEach(([key, { ref, placement='top' }]) => {
      const el = ref?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 12;
      next[key] = { rect, placement, margin };
    });
    setPositions(next);
  };

  React.useLayoutEffect(() => {
    compute();
    const handler = () => compute();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [targets]);

  // ensure cleanup if overlay unmounts
  React.useEffect(() => {
    return () => {
      try { window.speechSynthesis.cancel(); } finally { setActiveKey(null); }
    };
  }, []);

  const bubbles = Object.entries(targets).map(([key, { text }]) => {
    const pos = positions[key];
    if (!pos) return null;
    const { rect, placement, margin } = pos;

    const arrow = { ...overlayStyles.arrow };
    const bubble = { ...overlayStyles.bubble };

    if (placement === 'left') {
      bubble.top = rect.top + rect.height/2;
      bubble.left = rect.left - margin;
      bubble.transform = 'translate(-100%, -50%)';
      arrow.borderLeftColor = '#111';
      arrow.top = rect.top + rect.height/2;
      arrow.left = rect.left - 8;
      arrow.transform = 'translate(0, -50%) rotate(90deg)';
    } else if (placement === 'right') {
      bubble.top = rect.top + rect.height/2;
      bubble.left = rect.right + margin;
      bubble.transform = 'translate(0, -50%)';
      arrow.borderRightColor = '#111';
      arrow.top = rect.top + rect.height/2;
      arrow.left = rect.right + 8;
      arrow.transform = 'translate(0, -50%) rotate(-90deg)';
    } else if (placement === 'bottom') {
      bubble.top = rect.bottom + margin;
      bubble.left = rect.left + rect.width/2;
      bubble.transform = 'translate(-50%, 0)';
      arrow.borderTopColor = '#111';
      arrow.top = rect.bottom + 0;
      arrow.left = rect.left + rect.width/2;
      arrow.transform = 'translate(-50%, 0)';
    } else {
      bubble.top = rect.top - margin;
      bubble.left = rect.left + rect.width/2;
      bubble.transform = 'translate(-50%, -100%)';
      arrow.borderTopColor = '#111';
      arrow.top = rect.top - 8;
      arrow.left = rect.left + rect.width/2;
      arrow.transform = 'translate(-50%, 0) rotate(180deg)';
    }

    const handleTalk = () => {
      speak(text, {
        onStart: () => setActiveKey(key),
        onEnd: () => setActiveKey((k) => (k === key ? null : k)),
      });
    };

    const handleStop = () => {
      stop();
      setActiveKey(null);
    };

    return (
      <React.Fragment key={key}>
        <div style={arrow} />
        <div style={bubble}>
          <div>{text}</div>

          {/* Buttons row */}
          <div style={{display:'flex', gap:8, marginTop:8, alignItems:'center'}}>
            {/* Small image Read button */}
            <button
              style={overlayStyles.talkImgBtn}
              onClick={handleTalk}
              aria-label="Read this tip"
              title="Read"
            >
              <img
                src={talkBtnImage}
                alt="Read"
                style={overlayStyles.talkImg}
                draggable={false}
              />
            </button>

            {/* Red STOP only while this tip is speaking */}
            {activeKey === key && (
              <button
                style={overlayStyles.stopRedSmall}
                onClick={handleStop}
                aria-label="Stop reading"
                title="Stop"
              >
                ⏹ Stop
              </button>
            )}
          </div>
        </div>
      </React.Fragment>
    );
  });

  return (
    <>
      <div style={overlayStyles.layer} aria-hidden>{bubbles}</div>
      {canDismiss && (
        <button
          style={overlayStyles.dismissButton}
          onClick={() => { stop(); setActiveKey(null); onDismiss?.(); }}
        >
          Hide Tips
        </button>
      )}
    </>
  );
}



async function getEducatorByCode(db, code) {
  if (!code) return null;
  const q = query(
    collection(db, "users"),
    where("educatorCode", "==", code),
    // match your roles; adjust if you use an array vs string for role
    where("role", "in", ["educator", "ca-5th-educator"])
  );
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() };
}

// ⬇️ Add this helper near the top (below the function above)
function buildVisibilityFromScitools(topics, scitools) {
  // prefer slug mapping; default hidden if no explicit setting
  const visibleSlugSet = new Set(
    (Array.isArray(scitools) ? scitools : [])
      .filter(t => t && t.slug && (t.visible ?? true))
      .map(t => String(t.slug).trim().toLowerCase())
  );
  return topics.map(t =>
    visibleSlugSet.has(String(t.slug || "").trim().toLowerCase())
  );
}
// --- Speech/TTS helper (Web Speech API) ---
// --- Speech/TTS helper (Web Speech API) ---
// --- Speech/TTS helper (Web Speech API) ---
function useSpeech() {
  const [isSpeaking, setIsSpeaking] = React.useState(false);
  const voicesRef = React.useRef([]);
  const utterRef = React.useRef(null);

  const loadVoices = React.useCallback(() => {
    const v = window.speechSynthesis?.getVoices?.() || [];
    if (v.length) voicesRef.current = v;
  }, []);

  React.useEffect(() => {
    loadVoices();
    const onVoices = () => loadVoices();
    window.speechSynthesis?.addEventListener?.("voiceschanged", onVoices);
    return () => {
      window.speechSynthesis?.removeEventListener?.("voiceschanged", onVoices);
    };
  }, [loadVoices]);

  const pickVoice = React.useCallback(() => {
    const voices = voicesRef.current || [];
    if (!voices.length) return null;
    const saved = localStorage.getItem("ttsVoiceName");
    if (saved) {
      const match = voices.find(v => v.name === saved);
      if (match) return match;
    }
    const prefs = [
      "Google US English","Google en-US","Microsoft Aria","Microsoft Jenny",
      "Samantha","Karen","Serena","Moira","Daniel","Victoria","Tessa","Allison"
    ];
    const en = voices.filter(v => (v.lang || "").toLowerCase().startsWith("en"));
    for (const p of prefs) {
      const byName = en.find(v => v.name.includes(p)) || voices.find(v => v.name.includes(p));
      if (byName) return byName;
    }
    return en[0] || voices[0] || null;
  }, []);

  const chunkify = (text) =>
    String(text || "")
      .replace(/\s+/g, " ")
      .split(/(?<=[.!?])\s+/)
      .filter(Boolean)
      .map(s => s.trim());

  const speak = (text, opts = {}) => {
    try {
      window.speechSynthesis.cancel();

      const {
        rate = 0.92,
        pitch = 1.05,
        volume = 1,
        voiceName,
        lang = "en-US",
        onStart,   // <-- NEW
        onEnd,     // <-- NEW
      } = opts;

      const chosen =
        (voiceName && (voicesRef.current || []).find(v => v.name === voiceName)) ||
        pickVoice();

      const parts = chunkify(text);
      if (!parts.length) return;

      let idx = 0;
      const makeUtter = () => {
        if (idx >= parts.length) return null;
        const u = new SpeechSynthesisUtterance(parts[idx]);
        u.rate = rate; u.pitch = pitch; u.volume = volume;
        u.lang = chosen?.lang || lang;
        if (chosen) u.voice = chosen;

        u.onstart = () => { setIsSpeaking(true); onStart?.(); };
        u.onerror = () => { setIsSpeaking(false); onEnd?.(); };
        u.onend = () => {
          idx += 1;
          if (idx < parts.length) {
            setTimeout(() => {
              const next = makeUtter();
              if (next) window.speechSynthesis.speak(next);
            }, 120);
          } else {
            setIsSpeaking(false);
            onEnd?.();
          }
        };

        utterRef.current = u;
        return u;
      };

      const first = makeUtter();
      if (first) window.speechSynthesis.speak(first);
    } catch (e) {
      console.warn("Speech not supported or blocked by browser:", e);
      setIsSpeaking(false);
    }
  };

  const stop = () => {
    try { window.speechSynthesis.cancel(); } finally { setIsSpeaking(false); }
  };

  return { speak, stop, isSpeaking };
}


const Ca5thStudentOther = () => {
  const navigate = useNavigate();
  const [popupContent, setPopupContent] = useState(null);
  const [visibleTopics, setVisibleTopics] = useState(() => {
    const savedVisibility = localStorage.getItem("visibleTopicsOther");
    return savedVisibility ? JSON.parse(savedVisibility) : Array(27).fill(true);
  });
  const [formVisible, setFormVisible] = useState(false);
  const [educatorControlsVisible, setEducatorControlsVisible] = useState(false);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null); // Track the current user
  const [user, setUser] = useState(null); // Store user data from Firestore
  const [progressData, setProgressData] = useState({});
  const [educatorName, setEducatorName] = useState(null); // Store educator's name
  const { subject } = useParams();
  const db = getFirestore();
  const auth = getAuth();
  
  const { speak, stop } = useSpeech(); // put at top of Ca5thStudentOther
  const [speakingFor, setSpeakingFor] = useState(null); // slug of the speaking topic or null
// NEW: topics visibility gate
const [topicsVisible, setTopicsVisible] = React.useState(false);

// Replace your firstTopicRef with this pair:
const firstTopicAnchorRef = React.useRef(null);
const setFirstTopicAnchor = React.useCallback((el) => {
  if (!firstTopicAnchorRef.current && el) {
    firstTopicAnchorRef.current = el; // capture once (first visible topic)
  }
}, []);

// --- Overlay visibility + target refs
const [hideTips, setHideTips] = useState(() => {
  // remember preference across sessions
  return localStorage.getItem("studentHideTips") === "true";
});
const dismissTips = () => {
  setHideTips(true);
  localStorage.setItem("studentHideTips", "true");
};
const showTips = () => {
  setHideTips(false);
  localStorage.setItem("studentHideTips", "false");
};

// Refs for overlay anchors
const firstTopicRef = React.useRef(null);
// NEW: individual refs for each nav button
const homeRef = React.useRef(null);
const scienceRef = React.useRef(null);
const socialRef = React.useRef(null);
const mathRef = React.useRef(null);
const otherRef = React.useRef(null);


  const brightStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbright_star.png?alt=media&token=b033c5b7-788e-401c-b650-98399c1e8551";
  const blankStar = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fblank_star.png?alt=media&token=0dd0e339-3016-4f1c-8db2-eae91a16c21a";
  


  const handleOpenPopup = (content) => {
    setPopupContent(content);
  };

  const handleClosePopup = () => {
    stop();
    setPopupContent(null);
  };

  const getSubjectKey = (subject) => {
    switch (subject?.toLowerCase()) {
      case "other":
        return "ca_5th_other";
      case "socialstudies":
        return "ca_5th_socialstudies";
      case "math":
        return "ca_5th_math";
      default:
        return "ca_5th_science";
    }
  };

  const handleHomeClick = () => {
    stop();
    if (user) {
      const role = user.role.toLowerCase();
      if (role.includes("student")) {
        navigate("/dashboard");
      } else if (role.includes("educator")) {
        navigate("/educator/dashboard");
      } else if (role.includes("admin") || role.includes("administrator")) {
        navigate("/admin");
      } else {
        console.error("Unknown user role:", role);
      }
    } else {
      console.error("User is not defined.");
    }
  };

  const handleSubjectClick = (subject) => {
    stop();
    const role = "ca-5th-student"; // Replace with actual role logic
    const path = subject === "other" 
      ? `/student/ca/ca-5th-student-other` 
      : `/student/ca/ca-5th-student-${subject}`;
    navigate(path);
  };

  const handleGameClick = (gameUrl) => {
    stop();
    navigate(gameUrl);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userDocRef);

        if (userSnap.exists()) {
          const userData = userSnap.data(); // ✅ Properly declare userData
          setUser(userData); // Set user data from Firestore

          if (!userData.role) {
            console.error("User role is missing in Firestore document:", userData);
            setLoading(false);
            return;
          }

          if (userData.role.includes("student")) {
            // Fetch educator name using educatorCode from the users collection
            if (userData.educatorCode) {
              const educatorQuery = query(
                collection(db, "users"),
                where("educatorCode", "==", userData.educatorCode),
                where("role", "in", ["educator", "ca-5th-educator"]) // Adjust roles if needed
              );

              const educatorSnapshot = await getDocs(educatorQuery);
              if (!educatorSnapshot.empty) {
                const educatorData = educatorSnapshot.docs[0].data();
                setEducatorName(`${educatorData.sirName || ""} ${educatorData.lastName || ""}`.trim());
              }
            }
          }
        } else {
          console.error("No such user found in Firestore!");
        }
      } else {
        console.error("No user is signed in.");
      }

      setLoading(false); // ✅ Done with auth-related fetching
    });

    return () => unsubscribe();
  }, [auth, db]);

  // This section shows the educator's selected topics if they share the same educatorCode
  useEffect(() => {
    const fetchEducatorVisibility = async () => {
      if (!currentUser?.educatorCode) {
        setVisibleTopics(Array(topics.length).fill(true));
        setLoading(false); // ✅ Ensure loading is set to false
        return;
      }

      try {
        const educatorQuery = query(
          collection(db, "users"),
          where("educatorCode", "==", currentUser.educatorCode)
        );
        const snapshot = await getDocs(educatorQuery);
        if (!snapshot.empty) {
          const educatorDoc = snapshot.docs[0];
          const data = educatorDoc.data();
          const scitools = data?.scitools;

          if (Array.isArray(scitools) && scitools.length === topics.length) {
            const visibilityArray = scitools.map(t => t.visible ?? true);
            const gameVisibilityArray = scitools.map(t => t.gameVisible ?? true); // Add game visibility
            setVisibleTopics(
              topics.map((_, index) => visibilityArray[index] && gameVisibilityArray[index]) // Combine visibility
            );
          } else {
            setVisibleTopics(Array(topics.length).fill(true)); // Fallback to show all
          }
        } else {
          setVisibleTopics(Array(topics.length).fill(true)); // No educator match
        }
      } catch (err) {
        console.error("Error syncing with educator:", err);
        setVisibleTopics(Array(topics.length).fill(true));
      }

      setLoading(false); // ✅ Ensure loading is set to false after async logic
    };

    fetchEducatorVisibility();
  }, [currentUser, topics.length]);

  useEffect(() => {
    const fetchEducatorSettings = async () => {
      if (!user?.educatorCode) return;

      try {
        const educatorQuery = query(
          collection(db, "users"),
          where("educatorCode", "==", user.educatorCode),
          where("role", "in", ["educator", "ca-5th-educator"])
        );
        const snapshot = await getDocs(educatorQuery);
        if (!snapshot.empty) {
          const educatorDoc = snapshot.docs[0];
          const data = educatorDoc.data();
          const scitools = data?.scitools;

          if (Array.isArray(scitools) && scitools.length === topics.length) {
            const visibilityArray = scitools.map(t => t.visible ?? true);
            setVisibleTopics(visibilityArray);
          } else {
            setVisibleTopics(Array(topics.length).fill(true)); // Fallback to show all
          }
        } else {
          setVisibleTopics(Array(topics.length).fill(true)); // No educator match
        }
      } catch (err) {
        console.error("Error fetching educator settings:", err);
        setVisibleTopics(Array(topics.length).fill(true));
      }
      
    };

    fetchEducatorSettings();
  }, [user]); // Runs when the user state updates
// This section above shows the educator's selected topics if they share the same educatorCode
  
  

  const saveProgressToUserData = async (user, subject, slug, field) => {
    if (!user?.uid || !slug || !field || typeof subject !== "string") return;

    const userDataRef = doc(db, "userData", user.uid);
    const subjectKey = `${user.state.toLowerCase()}_${user.grade.toLowerCase()}_${subject.toLowerCase()}`; // Fixed template literal

    await setDoc(
      userDataRef,
      {
        [subjectKey]: {
          [slug]: {
            [field]: true,
          },
        },
      },
      { merge: true }
    );

    // When setting user progress, do this once if not stored already
    await setDoc(
      userDataRef,
      {
        sirName: user.sirName,
        firstName: user.firstName,
        lastName: user.lastName,
        state: user.state,
        city: user.city,
        zip: user.zip,
      },
      { merge: true }
    );
  };

  const fetchProgress = async () => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
  
    if (!currentUser?.uid || !user?.state || !user?.grade) return;
  
    try {
      const subjectKey = `${user.state.toLowerCase()}_${user.grade.toLowerCase()}_other`;
      const userDataRef = doc(db, "userData", currentUser.uid);
      const userDataSnap = await getDoc(userDataRef);
      const userData = userDataSnap.data() || {};
  
      const videoMusicData = userData[subjectKey] || {};
  
      const progressMap = {};
      topics.forEach((topic) => {
        const slug = topic.slug;
        if (!slug) return;
  
        const vmEntry = videoMusicData[slug] || {};
  
        const gameValue = vmEntry?.game;
        const isGameComplete = typeof gameValue === "boolean"
          ? gameValue
          : Boolean(gameValue?.completed);
  
        const score = typeof gameValue === "object" && gameValue?.score
          ? gameValue.score
          : 0;
  
        progressMap[slug] = {
          video: Boolean(vmEntry?.video),
          music: Boolean(vmEntry?.music),
          game: isGameComplete,
          score,
        };
      });
  
      setProgressData(progressMap);
    } catch (error) {
      console.error("Error fetching user progress:", error);
    }
  };
  
  

  useEffect(() => {
    fetchProgress();
  }, [user]);

  useEffect(() => {
    const fetchStudents = async (educatorCode) => {
      if (!educatorCode) return;
      try {
        const studentsQuery = query(
          collection(db, "users"),
          where("role", "==", "ca-5th-student"),
          where("educatorCode", "==", educatorCode)
        );
        const snapshot = await getDocs(studentsQuery);
        const studentsData = snapshot.docs.map((doc) => doc.data());
        setStudents(studentsData);
      } catch (error) {
        console.error("Error fetching students:", error);
      }
    };

    if (user?.educatorCode) {
      fetchStudents(user.educatorCode);
    }
  }, [user]);

  const handleCheckboxChange = (index) => {
    const updatedVisibleTopics = [...visibleTopics];
    updatedVisibleTopics[index] = !updatedVisibleTopics[index];
    setVisibleTopics(updatedVisibleTopics);
    localStorage.setItem("visibleTopicsOther", JSON.stringify(updatedVisibleTopics));
  };

  const handleSaveChanges = async () => {
    if (!user || !user.uid) {
      console.error("User UID is not defined.");
      return;
    }
  
    if (!topics || !Array.isArray(topics)) {
      console.error("Topics is not defined or not an array.");
      return;
    }
  
    const updatedTools = topics.map((topic, index) => ({
      title: topic.title,
      visible: visibleTopics[index] ?? true,  // Ensure visibleTopics[index] exists
    }));
  
    try {
      const userDocRef = doc(db, "users", user.uid); // Ensure user.uid is defined
      await updateDoc(userDocRef, { scitools: updatedTools });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  useEffect(() => {
    return () => window.speechSynthesis.cancel(); // or stop()
  }, []); // <-- empty deps
  
  
  const backgroundImage = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fbg-texture-light-yellow.png?alt=media&token=0da7dd85-e949-42dd-acfb-9771c641b76f";

  const uniqueStandardNumbers = Array.from(
    new Set(topics.map(topic => topic.standardNumber))
  );

  const standardNumberColorMap = {};
  uniqueStandardNumbers.forEach((number, index) => {
    const colorIndex = index % styles.topicColors.length;
    standardNumberColorMap[number] = styles.topicColors[colorIndex];
  });

  // Optional: Add debug logging
  console.log("loading:", loading);
  console.log("visibleTopics:", visibleTopics);

  if (loading || !topics.length || !visibleTopics.length) {
    return (
      <div className="text-center mt-10 text-xl font-bold text-blue-500">
        Loading...
      </div>
    );
  }

  const subjectKeyFull = getSubjectKey(subject);

  return (
    <div
      style={{
        ...styles.container,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        background: backgroundImage
          ? undefined
          : "linear-gradient(to bottom, #6dd5fa, #2980b9)",
      }}
    >
      <img
        src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
        alt="Logo"
        style={styles.logo}
      />

<div style={{ ...styles.navButtons, marginBottom: "40px" }}>
  <button ref={homeRef} onClick={handleHomeClick} style={styles.homeButton}>Home</button>
  <button ref={scienceRef} onClick={() => handleSubjectClick("science")} style={styles.scienceButton}>Science</button>
  <button ref={socialRef} onClick={() => handleSubjectClick("socialstudies")} style={styles.socialStudiesButton}>Social Studies</button>
  <button ref={mathRef} onClick={() => handleSubjectClick("math")} style={styles.mathButton}>Math</button>
  <button ref={otherRef} onClick={() => handleSubjectClick("other")} style={styles.otherButton}>Other</button>
</div>


<h1 style={styles.title}>
  {user?.firstName && (
    <span style={{
      fontSize: "22px",
      display: "inline-block",
      marginBottom: "10px",
      padding: "5px 15px",
      backgroundColor: "#fef3c7", // light yellow
      color: "#b45309",
      fontWeight: "600",
      borderRadius: "6px",
      textTransform: "uppercase",
      letterSpacing: "1px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }}>
      {user.firstName}'s Student Portal
    </span>
  )}
  <br />
  <span style={{
    fontSize: "40px",
    fontWeight: "bold",
    color: "#0a3d62",
    textShadow: "1px 1px 2px rgba(0,0,0,0.2)",
    display: "block",
    marginBottom: "12px",
  }}>
    Virginia 5th Grade Other
  </span>
  {educatorName && (
    <span style={{
      fontSize: "18px",
      display: "inline-block",
      marginTop: "8px",
      padding: "4px 12px",
      backgroundColor: "#e0f2fe", // light blue
      color: "#0369a1",
      fontWeight: "500",
      borderRadius: "6px",
      fontStyle: "italic",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
    }}>
      Welcome to {educatorName}'s Class
    </span>
  )}
</h1>

<p style={styles.subtitle}>
  Watch the video or listen to the song to explore new concepts,
  then play the game to reinforce learning in a fun way!
</p>

      {/* Topics */}
      <div style={styles.topicsContainer}>
     {topics.map((topic, index) =>
  visibleTopics[index] && topic.title?.trim() ? (
    <div
  key={index}
  ref={(() => {
    // attach only to the first visible topic
    if (!firstTopicRef.current) return firstTopicRef;
    return null;
  })()}
  style={{
    ...styles.topicCard,
    borderColor: standardNumberColorMap[topic.standardNumber] || "#ccc"
  }}
>

      <div style={styles.topicContent}>
        {/* Completion Stars */}
        <div style={{ display: "flex", justifyContent: "center", gap: "5px", marginBottom: "5px" }}>
          {/* Video Star */}
          <img
            title={progressData[topic.slug]?.video ? "✅ Video Completed" : "⏳ Video Not Started"}
            src={progressData[topic.slug]?.video ? brightStar : blankStar}
            alt="Video Star"
            style={{ width: 20, height: 20, cursor: "help" }}
          />
          {/* Music Star */}
          <img
            title={progressData[topic.slug]?.music ? "✅ Music Completed" : "⏳ Music Not Started"}
            src={progressData[topic.slug]?.music ? brightStar : blankStar}
            alt="Music Star"
            style={{ width: 20, height: 20 }}
          />
          {/* Game Star */}
          <img
            title={
              progressData[topic.slug]?.game
                ? `✅ Game Completed (Score: ${progressData[topic.slug]?.score || 0})`
                : "⏳ Game Not Started"
            }
            src={progressData[topic.slug]?.game ? brightStar : blankStar}
            alt="Game Star"
            style={{ width: 20, height: 20 }}
          />
        </div>
        <div style={styles.topicHeaderRow}>
  <h3 style={styles.topicTitle} aria-label={`${topic.title} topic title`}>
    {topic.title}
  </h3>

  <div style={styles.talkGroup}>
    <button
      onClick={(e) => {
        e.stopPropagation();
        speak(`${topic.title}. ${topic.description || ''}`, {
          onStart: () => setSpeakingFor(topic.slug),
          onEnd: () => setSpeakingFor(null),
        });
      }}
      onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(2px)')}
      onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
      onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
      title="Tap to hear this topic"
      aria-label={`Tap to hear: ${topic.title}`}
      style={styles.talkImageButton}
    >
      <img
        src={talkBtnImage}
        alt="Talk"
        style={styles.talkImage}
        draggable={false}
      />
    </button>

    {speakingFor === topic.slug && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          stop();
          setSpeakingFor(null);
        }}
        aria-label="Stop speaking"
        title="Stop"
        style={styles.stopRedButton}
      >
        ⏹ Stop
      </button>
    )}
  </div>
</div>



        {topic.subtitle1 && <p style={styles.subtitle1}>{topic.subtitle1}</p>}
        <p style={styles.subtitle2}>Topic: {index + 1}</p>   
        <p style={styles.topicDescription}>
          <span style={styles.standardNumber}>{topic.standardNumber}</span> {topic.description}
        </p>
      </div>


      <div style={styles.topicButtons}>
      {topic.video && (
  <button
    style={styles.button}
    onClick={() => {
      stop(); // <<< NEW
      handleOpenPopup(
        <VideoTool
          videoUrl={topic.video}
          subjectKey={`${user.state.toLowerCase()}_${user.grade.toLowerCase()}_other`}
          slug={topic.slug}
          onProgressUpdate={fetchProgress}
        />
      );
    }}
  >
            <img
              src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fvideo-topic-image-button2.png?alt=media&token=c787b8ef-9cfa-4051-ae09-9d58cee36987"
              alt="Video"
              style={{ width: "20px", height: "20px", marginRight: "8px" }}
            />
            Video
          </button>
        )}
        {topic.music && (
  <button
    style={styles.button}
    onClick={() => {
      stop(); // <<< NEW
      handleOpenPopup(
        <div>
          <MusicTool
            musicUrl={topic.music}
            subjectKey={`${user.state.toLowerCase()}_${user.grade.toLowerCase()}_other`}
            slug={topic.slug}
            onProgressUpdate={fetchProgress}
          />
          <div style={styles.lyricsContainer}>
            {topic.lyrics.map((line, idx) => (
              <p key={idx} style={styles.lyricLine}>{line}</p>
            ))}
          </div>
        </div>
      );
    }}
  >
    🎵 Music
  </button>
)}
        {topic.slug && topic.gameType && visibleTopics[index] && ( // Ensure game visibility
          <button
            style={styles.button}
            onClick={() => handleGameClick(`/treasure-map/other/${topic.slug}`)}
          >
            🎮 Game
          </button>
        )}
      </div>
    </div>
  ) : null
)}

      </div>

      {popupContent && (
        <div style={styles.popupOverlay}>
          <div style={styles.popupBox}>
            <button onClick={handleClosePopup} style={styles.closeButton}>
              ✖
            </button>
            {popupContent}
          </div>
        </div>
      )}


  <div style={styles.studentContainer}>
    <h2 style={{ fontSize: "24px", color: "#333", marginBottom: "20px" }}>
      Classmates!
    </h2>
    {students.length === 0 ? (
      <p style={{ fontSize: "16px", color: "#555" }}>
        No students found with your educator code.
      </p>
    ) : (
      <ul style={{ listStyleType: "none", padding: 0 }}>
        {students.map((student, index) => (
          <li key={index} style={styles.studentItem}>
            <div style={styles.studentRow}>
              <img
                src={student.avatarUrl || "https://via.placeholder.com/50"} // Fallback avatar
                alt={`${student.firstName}'s avatar`}
                style={styles.avatarGrid}
              />
              <div>
                <span style={styles.studentName}>
                  {student.firstName} {student.lastName?.charAt(0)}.
                </span>
                {student.role === "ca-5th-educator" && (
                  <span style={{ color: "#2f855a", fontWeight: "bold", marginLeft: "8px" }}>
                    (Educator)
                  </span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    )}

{!hideTips && (
  <TalkHintsOverlay
    canDismiss
    onDismiss={dismissTips}
    targets={{
      // nav button tips (explicit alternating placements)
      home: {
        ref: homeRef,
        placement: 'bottom',
        text: 'Click this purple button to go home.',
      },
      science: {
        ref: scienceRef,
        placement: 'top',
        text: 'Click this blue button to go to Science.',
      },
      social: {
        ref: socialRef,
        placement: 'bottom',
        text: 'Click this green button to go to Social Studies.',
      },
      math: {
        ref: mathRef,
        placement: 'top',
        text: 'Click this red button to go to Math.',
      },
      other: {
        ref: otherRef,
        placement: 'bottom',
        text: 'Click this yellow button to go to Other.',
      },

      // first topic tip above the first topic container
      firstTopic: {
        ref: firstTopicRef,
        placement: 'top',
        text:
          'Click the Video button to play the video to collect the video star. ' +
          'Click the Music button to play and learn the song to collect the music star. ' +
          'Take the quiz and play the game and try to score high to collect the game star.',
      },
    }}
  />
)}


{hideTips && (
  <button
    style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      zIndex: 10000,
      background: '#fff',
      color: '#111',
      border: '2px solid #111',
      borderRadius: 999,
      padding: '10px 16px',
      fontWeight: 700,
      cursor: 'pointer',
      boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
    }}
    onClick={showTips}
  >
    Show Tips
  </button>
)}


  <footer style={{
  fontSize: "22px",
  display: "inline-block",
  marginBottom: "10px",
  padding: "5px 15px",
  backgroundColor: "#fef3c7", // light yellow
  color: "#b45309",
  fontWeight: "600",
  borderRadius: "6px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  textAlign: "center"
}}>
  <p style={{ margin: 0 }}>© Smart Board Learning™</p>
  <p style={{ margin: 0 }}>All rights reserved.</p>
</footer>
</div>
    </div>
  );
};

const baseNavButton = {
  color: "#fff",
  padding: "10px 20px",
  fontSize: "25px",
  border: "none",
  borderRadius: "5px",
  cursor: "pointer",
  width: "200px",
  height: "200px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  textAlign: "center",
  boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)", // Layered shadow
  border: "2px solid #222", // Border for depth
  transition: "transform 0.2s, box-shadow 0.2s",
};

const styles = {
  container: {
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
    minHeight: "100vh", // Ensure the background covers the full viewport height
    padding: "20px",
    backgroundSize: "cover", // Ensure the background image or gradient covers the entire container
    backgroundPosition: "center",
    backgroundColor: "#f0f4f8", // Softer background color
    background: "radial-gradient(circle at 30% 30%, #d0eaff, #2980b9)", // Updated gradient
  },
  footer: {
    marginTop: "40px",
    fontSize: "20px",
    color: "#000",
    textAlign: "center",
  },
  logo: {
    width: "150px",
    margin: "0 auto",
    display: "block",
    transform: "rotate(-2deg)", // Slight tilt
    filter: "drop-shadow(2px 4px 2px rgba(0,0,0,0.4))", // Drop shadow for bounce effect
  },
  navButtons: {
    display: "flex",
    justifyContent: "center",
    marginTop: "20px",
    gap: "10px",
  },
  navButton: baseNavButton, // Base style for nav buttons
  homeButton: {
    ...baseNavButton,
    backgroundColor: "#A020F0", // Purple color
  },
  scienceButton: {
    ...baseNavButton,
    backgroundColor: "#4a90e2", // Blue color
  },
  socialStudiesButton: {
    ...baseNavButton,
    backgroundColor: "#4CAF50", // Green color
  },
  mathButton: {
    ...baseNavButton,
    backgroundColor: "#FF6347", // Reddish color
  },
  otherButton: {
    ...baseNavButton,
    backgroundColor: "#FFD700", // Yellowish color
  },
  title: {
    fontSize: "35px",
    color: "#000", // Change color to black
    marginTop: "20px",
  },
  subtitle: {
    fontSize: "20px",
    color: "#555", // Softer subtitle color
    marginBottom: "20px",
  },
  topicsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "20px",
    padding: "20px",
  },
  topicCard: {
    backgroundColor: "rgba(255, 255, 255, 0.3)", // Semi-transparent white background
    borderRadius: "10px",
    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)", // Layered shadow for depth
    padding: "20px",
    textAlign: "center",
    backgroundImage: 
      `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), 
      url('https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fother-bg-texture-topic.png?alt=media&token=246eefc3-c66d-4daf-8bfb-af8f5058a34e')`
    , // Add faded overlay
    backgroundSize: "cover",
    backgroundBlendMode: "overlay", // Blend the gradient and image
    border: "5px solid", // Add border
  },
  topicTitle: {
    fontSize: "22px",
    color: "#333",
    marginBottom: "10px",
    fontFamily: "'Quicksand', sans-serif", // Playful font without cursive
  },
  topicContent: {
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  titleImage: {
    width: "100%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  subtitle1: {
    fontSize: "18px",
    color: "#777", // Softer subtitle color
    fontStyle: "italic",
  },
  subtitle2: {
    fontSize: "14px",
    color: "#555",
  },
  topicDescription: {
    fontSize: "14px",
    color: "#555", // Softer description color
    textAlign: "center",
  },
  topicButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  button: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px",
    fontSize: "14px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    flex: "1",
    margin: "0 5px",
    boxShadow: "0 4px 0 #1e5aa8, 0 6px 10px rgba(0,0,0,0.25)", // Blue shadow
    border: "2px solid #1e5aa8", // Blue border
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  ":hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 12px rgba(0, 0, 0, 0.4), 0 4px 6px rgba(0, 0, 0, 0.2)", // Enhanced shadow on hover
  },
  ":active": {
    transform: "translateY(0)",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.2)", // Subtle shadow on click
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    position: "relative",
    padding: "56px 20px 20px", // extra top padding for the X
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "600px",
    width: "90%",
  },
  closeButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    zIndex: 2,                 // make sure it's above the audio layer
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: "30px",
    height: "30px",
    fontSize: "18px",
    cursor: "pointer",
    textAlign: "center",
    lineHeight: "30px",
  },
  // A fixed 16:9 stage. Every video renders in an identically sized frame
  // regardless of its source dimensions — a portrait clip gets black bars on
  // the sides instead of stretching the popup taller than the screen, which
  // is what forced people to scroll.
  mediaStage: {
    width: "100%",
    aspectRatio: "16 / 9",
    background: "#000",
    borderRadius: "8px",
    overflow: "hidden",
    marginTop: "10px",
  },
  media: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    display: "block",
    border: "none",
    background: "#000",
  },
  // Audio has no intrinsic height, so it keeps a simple full-width rule.
  mediaAudio: {
    width: "100%",
    marginTop: "10px",
  },
  lyricsContainer: {
    marginTop: "20px",
    textAlign: "center",
    maxHeight: "500px", // Set a maximum height
    overflowY: "auto", // Enable vertical scrolling
  },
  lyricLine: {
    fontSize: "16px",
    color: "#333",
    margin: "5px 0",
  },
  educatorControls: {
    backgroundColor: "#e0e7ef", // Softer background color for educator controls
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
  },
  controlsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  },
  controlItem: {
    textAlign: "left",
  },
  formContainer: {
    backgroundColor: "#f9f9f9",
    padding: "30px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 2)",
  },
  formRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "30px",
  },
  formField: {
    flex: "1 1 200",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  input: {
    width: "80%",
    padding: "10px",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "5px",
  },
  textarea: {
    width: "80%",
    padding: "10",
    borderRadius: "5px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    border: "1px solid #ccc",
    marginTop: "10px",
    minHeight: "100px",
  },
  payButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
  },
  requestButton: {
    backgroundColor: "#ffa500", // Orange color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #cc7000, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #b86e00", // Border for depth
    marginTop: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cancelButton: {
    backgroundColor: "#ff4d4d",
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
    marginTop: "20px",
    marginLeft: "10px",
  },
  educatorControlsButton: {
    backgroundColor: "#A020F0", // Purple color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #5e1280, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #4a0e60", // Border for depth
    marginTop: "20px",
    marginLeft: "10px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "20px",
    marginTop: "20px",
  },
  studentProgressButton: {
    backgroundColor: "#ff69b4", // Pink color
    color: "#fff",
    width: "200px",
    height: "200px",
    fontSize: "20px",
    fontWeight: "bold",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 6px 0 #b4004b, 0 8px 10px rgba(0,0,0,0.4)", // Layered shadow
    border: "2px solid #8e0038", // Border for depth
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "20px", // Add marginTop to align the button
  },
  studentProgress: {
    backgroundColor: "#f9f9f9",
    padding: "20px",
    borderRadius: "10px",
    marginTop: "20px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  studentButtons: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "center",
  },
  studentButton: {
    backgroundColor: "#4a90e2", // Softer blue color
    color: "#000",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 2)",
  },
  saveButton: {
    backgroundColor: "linear-gradient(to bottom, #4caf50, #388e3c)", // Match button style
    color: "#fff",
    padding: "10px 20px",
    fontSize: "16px",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    boxShadow: "0 4px 0 #2e7d32, 0 6px 10px rgba(0,0,0,0.25)", // Layered shadow
    border: "2px solid #1b5e20", // Border for depth
    marginTop: "20px",
  },
  standardNumber: {
    fontWeight: "bold",
    color: "#222",
    marginRight: "5px",
  },
  studentRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 14px",
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
    marginBottom: "10px",
    justifyContent: "flex-start",
    minHeight: "50px"
  },
  
  studentItem: {
    fontSize: "18px",
    color: "#333",
  },
  
  studentName: {
    fontWeight: "500",
    fontSize: "18px",
    color: "#2c3e50",
  },
  studentContainer: {
    backgroundColor: "#e6f7ff",
    padding: "20px",
    borderRadius: "10px",
    marginBottom: "30px",
  },
  
  studentGrid: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "15px",
  },
  
  studentCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    padding: "10px",
    width: "130px",
    textAlign: "center",
  },
  
  avatarGrid: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    objectFit: "cover",
    marginBottom: "8px",
  },
  
  studentText: {
    fontSize: "14px",
    color: "#333",
  },
  topicHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    marginBottom: 8,
  },
  
  talkButtonKid: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    color: "#111",
    border: "3px solid #111",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 4px 0 rgba(0,0,0,0.25)",
    transition: "transform 0.1s",
  },
  talkButtonKidIcon: {
    width: 22,
    height: 22,
    display: "inline-block",
  },
  talkButtonKidPressed: {
    transform: "translateY(2px)",
  },
  
  talkImageButton: {
    // reuse your pill/button look:
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    // optional: keep the same outline/press effect as the old button
    borderRadius: 999,
    boxShadow: "0 4px 0 rgba(0,0,0,0.25)",
    transform: "translateY(0)",
  },
  
  talkImage: {
    width: 120,      // tweak to taste
    height: "auto",
    display: "block",
    // optional drop shadow for pop:
    filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.25))",
  },
  talkGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,                 // side-by-side
    // If you want the Stop button UNDER the talk button instead, use:
    // flexDirection: "column",
  },
  
  stopRedButton: {
    background: "#ff4d4d",
    color: "#fff",
    border: "3px solid #111",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 14,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 4px 0 rgba(0,0,0,0.25)",
    transform: "translateY(0)",
  },
  
  
  topicColors: [
    "#FF6347",  // Reddish
    "#4CAF50",  // Green
    "#FFD700",  // Yellow
    "#4a90e2",  // Blue
    "#A020F0",  // Purple
    "#FF69B4",  // Pink
    "#FFA500",  // Orange
    "#2980b9",  // Deep Blue
    "#00CED1",  // Dark Turquoise (new)
    "#8B4513",  // Saddle Brown (new)
  ],
};

export default Ca5thStudentOther;



